"use client";

// Career Launch 로그인 — 리뉴얼 auth 레이아웃(Toss 톤) 재사용. 학생 전용 프로그램이라
// 기업(PARTNER) 계정은 차단하고, 학생·운영자만 대시보드로 진입시킨다.
import type { FormEvent } from "react";
import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuthSession } from "../auth/AuthSessionProvider";
import { AuthApiError, loginWithEmail, clearAccessToken } from "../../lib/auth-client";
import { useLaunchT } from "../../lib/launch/i18n";
import { TalentButton } from "../talent/TalentButton";
import { TalentField, talentInputClass, authSubmitClass } from "../talent/auth/TalentAuthLayout";
import { TalentSocialButtons, TalentOrDivider } from "../talent/auth/TalentSocialButtons";
import { CareerLaunchIntro } from "./CareerLaunchIntro";
import { CareerLaunchHeader } from "./CareerLaunchHeader";
import { LaunchAmbientBackground } from "./LaunchAmbientBackground";
import { AplyFooter } from "../AplyFooter";

export function CareerLaunchLoginPage() {
  const t = useLaunchT();
  const router = useRouter();
  const { setAuthenticatedUser } = useAuthSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const { user } = await loginWithEmail({ email, password });
      // 학생 전용 — 기업 계정은 차단(운영자는 슈퍼유저로 학생 화면 접근 허용).
      if (user.role === "PARTNER") {
        clearAccessToken();
        setErrorMessage(
          t(
            "Career Launch는 학생 전용 프로그램이에요. 기업 계정으로는 이용할 수 없어요.",
            "Career Launch is for students only. Business accounts can't access it.",
            "Career Launch 是仅面向学生的项目，企业账号无法使用。",
            "Career Launch chỉ dành cho sinh viên. Tài khoản doanh nghiệp không thể sử dụng.",
            "Career Launchは学生専用プログラムです。企業アカウントではご利用いただけません。",
            "Career Launch hanya untuk mahasiswa. Akun bisnis tidak bisa mengakses."
          )
        );
        return;
      }
      setAuthenticatedUser(user);
      router.push("/career-launch/dashboard");
      router.refresh();
    } catch (error) {
      if (error instanceof AuthApiError && error.code === "EMAIL_VERIFICATION_REQUIRED") {
        const params = new URLSearchParams();
        if (email.trim()) params.set("email", email.trim());
        router.push(`/signup/verify-email?${params.toString()}`);
        router.refresh();
        return;
      }
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("로그인에 실패했어요. 다시 시도해 주세요.", "Login failed. Please try again.", "登录失败，请重试。", "Đăng nhập thất bại. Vui lòng thử lại.", "ログインに失敗しました。もう一度お試しください。", "Gagal masuk. Silakan coba lagi.")
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="isolate flex min-h-screen flex-col bg-white">
      <LaunchAmbientBackground />
      <CareerLaunchHeader />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-5xl px-5 pb-16 pt-6 md:py-14">
          <div className="grid gap-6 md:grid-cols-2 md:items-start md:gap-10">
            {/* 왼쪽 — 서비스 소개(무슨 서비스인지 인지) */}
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--v2-accent-weak)] px-3 py-1 text-[13px] font-bold text-[color:var(--v2-accent)]">
                ✨ APLY Career Launch
              </span>
              <h1 className="mt-4 whitespace-pre-line break-keep text-[26px] font-bold leading-[1.38] tracking-[-0.02em] text-[color:var(--v2-ink)] md:text-[34px] md:leading-[1.3]">
                {t(
                  "모든 취업 준비생을 위한\n취업 준비 Career Launch",
                  "Career Launch — job prep\nfor every job seeker",
                  "为所有求职者打造的\n求职准备 Career Launch",
                  "Career Launch — chuẩn bị việc làm\ncho mọi người tìm việc",
                  "すべての就活生のための\n就職準備 Career Launch",
                  "Career Launch — persiapan kerja\nuntuk semua pencari kerja"
                )}
              </h1>
              <CareerLaunchIntro className="mt-6" />
            </div>

            {/* 오른쪽 — 로그인 카드 */}
            <div className="rounded-3xl bg-white py-2 md:p-8 md:ring-1 md:ring-[color:var(--v2-line)]">
              <h2 className="text-[22px] font-bold tracking-[-0.02em] text-[color:var(--v2-ink)]">{t("로그인", "Sign in", "登录", "Đăng nhập", "ログイン", "Masuk")}</h2>
              <p className="mt-1.5 text-[15px] leading-[1.6] text-[color:var(--v2-ink-3)]">{t("학생 계정으로 로그인하고 이어서 진행해요.", "Sign in with your student account to continue.", "使用学生账号登录以继续。", "Đăng nhập bằng tài khoản sinh viên để tiếp tục.", "学生アカウントでログインして続けましょう。", "Masuk dengan akun mahasiswa untuk melanjutkan.")}</p>

              <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                <TalentField label={t("이메일", "Email", "邮箱", "Email", "メール", "Email")}>
                  <input
                    type="email"
                    placeholder={t("이메일 주소", "Email address", "邮箱地址", "Địa chỉ email", "メールアドレス", "Alamat email")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={talentInputClass}
                    required
                    autoComplete="email"
                  />
                </TalentField>
                <TalentField label={t("비밀번호", "Password", "密码", "Mật khẩu", "パスワード", "Kata sandi")}>
                  <input
                    type="password"
                    placeholder={t("비밀번호", "Password", "密码", "Mật khẩu", "パスワード", "Kata sandi")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={talentInputClass}
                    required
                    autoComplete="current-password"
                  />
                </TalentField>
                {errorMessage ? <p className="text-[14px] font-medium text-[color:var(--v2-attention)]">{errorMessage}</p> : null}
                <TalentButton type="submit" disabled={isSubmitting} variant="primary" size="lg" fullWidth className={authSubmitClass} aria-label={t("로그인", "Sign in", "登录", "Đăng nhập", "ログイン", "Masuk")}>
                  {isSubmitting ? t("로그인 중…", "Logging in…", "登录中…", "Đang đăng nhập…", "ログイン中…", "Sedang masuk…") : t("로그인", "Sign in", "登录", "Đăng nhập", "ログイン", "Masuk")}
                </TalentButton>
              </form>

              <TalentOrDivider />
              <TalentSocialButtons />

              <p className="mt-6 text-center text-[15px] text-[color:var(--v2-faint)]">
                {t("아직 계정이 없나요?", "Don't have an account yet?", "还没有账号？", "Chưa có tài khoản?", "アカウントがありませんか？", "Belum punya akun?")}{" "}
                <Link href="/talent/signup?next=/career-launch/dashboard" className="font-semibold text-[color:var(--v2-accent)]">
                  {t("학생으로 가입하기", "Sign up as a student", "以学生身份注册", "Đăng ký với tư cách sinh viên", "学生として登録", "Daftar sebagai mahasiswa")}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      <AplyFooter />
    </div>
  );
}
